import { types, Instance } from 'mobx-state-tree'
import moment from 'moment-timezone'
import UserConversationsApi from '../../../api/users/UserConversationsApi'
import { LoadableStatus } from '../helpers'
import Editable from '../helpers/Editable'
import { Alertable } from '../helpers/Alertable'
import { UserBase } from './UserBase'

export const UserConversation = types
  .compose(
    'UserConversation',
    Editable,
    Alertable,
    types.model({
      id: types.identifierNumber,
      userId: types.number,
      conversationId: types.number,
      topicId: types.number,
      hostId: types.number,
      guestToken: types.maybeNull(types.string),
      attended: types.maybeNull(types.boolean),
      startsAt: types.maybeNull(types.string),
      endsAt: types.maybeNull(types.string)
    })
  )
  .actions((self) => ({
    pastConversation: () => {
      return moment(self.endsAt) < moment()
    },
    /**
     * `pastConversation()` asks whether the dinner is OVER. Stories are now
     * collected *during* it -- someone scans the QR at the table -- so the
     * gate on the Stories section needs the looser question: has this
     * conversation started, or is it happening at some point today?
     *
     * Same shape as `Conversation.expired` / `Conversation.dayBefore`: a named
     * moment comparison living on the model, not date arithmetic at the call
     * site. The `endsAt` clause is kept so anything `pastConversation()` used
     * to allow still passes, even for a record with no `startsAt`.
     *
     * Note: the day comparison runs in the viewer's local timezone, not the
     * conversation's. `UserConversation` carries no timezone -- only
     * `Conversation` does -- and the practical case (a guest in the room) has
     * them in the same one.
     */
    currentOrPastConversation: () => {
      const now = moment()
      return moment(self.endsAt) < now || moment(self.startsAt) <= now || moment(self.startsAt).isSame(now, 'day')
    },
    updatedAttendance: (attended) => {
      return UserConversationsApi.update(self.conversationId, self.id, { attended }).then(({ response: { ok }, json }) => {
        if (ok) {
          // self.setUserConversations(json)
          self.updateMe(json)
          self.showAlert('Updated')
        }
      })
    }
  }))

export const UserConversations = types
  .compose(
    'UserConversations',
    UserBase,
    LoadableStatus,
    types.model({
      userConversations: types.optional(types.array(UserConversation), [])
    })
  )
  .actions((self) => ({
    setUserConversations(data: any) {
      self.userConversations.replace(data)
    }
  }))
  .actions((self) => ({
    loadUserConversations: () => {
      if (self.isLoading('userConversations')) {
        return
      }
      self.startLoading('userConversations')
      return UserConversationsApi.getAll(self.id).then(({ response: { ok }, json }) => {
        if (ok) {
          self.setUserConversations(json)
        }
        self.stopLoading('userConversations')
      })
    }
  }))
  .actions((self) => ({
    attendedTopic: (topicId) => {
      return self.userConversations.find((uc) => uc.topicId === topicId)
    }
  }))

export interface IUserConversation extends Instance<typeof UserConversation> {}
export interface IUserConversations extends Instance<typeof UserConversations> {}
export default UserConversations
